/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import type { SavedViewType } from "./";
import { useSavedViewsUnified } from "./use-saved-views-unified";

// ============================================================================
// TYPES
// ============================================================================

export interface SavedViewFilters {
  status?: string;
  search?: string;
  ownerId?: string;
  stage?: string;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  projectType?: string;
  tags?: string;
  [key: string]: unknown;
}

export interface SavedViewActionsOptions<TFilters extends SavedViewFilters> {
  /**
   * View type identifier (e.g., "projects", "clients", "suppliers", "documents")
   * Determines which storage backend to use (server vs local storage)
   */
  viewType: SavedViewType;

  /**
   * Current filter state
   */
  currentFilters: TFilters;

  /**
   * Callback to apply individual filter values
   */
  onApplyFilter: (filters: Partial<TFilters>) => void;

  /**
   * Callback to clear all filters
   */
  onClearAllFilters: () => void;

  /**
   * Callback to reset page to 1
   */
  onResetPage: () => void;

  /**
   * Optional: Include organization presets
   */
  includeOrgPresets?: boolean;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for managing saved view actions (apply, save, update, delete)
 *
 * Provides a clean interface for working with saved views including
 * applying filters from saved views and managing view lifecycle.
 *
 * @example
 * ```tsx
 * const { applyView, saveCurrentView, deleteCurrentView } = useSavedViewActions({
 *   viewType: "projects", // Required: determines storage backend
 *   currentFilters: myFilters,
 *   onApplyFilter: (filters) => setFilters(filters),
 *   onClearAllFilters: clearAll,
 *   onResetPage: () => setPage(1),
 * });
 * ```
 *
 * @example
 * ```tsx
 * // For local storage view types, you may also need storageKey
 * const { applyView, saveCurrentView, deleteCurrentView } = useSavedViewActions({
 *   viewType: "suppliers", // Local storage type
 *   currentFilters: myFilters,
 *   onApplyFilter: (filters) => setFilters(filters),
 *   onClearAllFilters: clearAll,
 *   onResetPage: () => setPage(1),
 * });
 * ```
 */
export function useSavedViewActions<TFilters extends SavedViewFilters>({
  viewType,
  currentFilters,
  onApplyFilter,
  onClearAllFilters,
  onResetPage,
  includeOrgPresets = true,
}: SavedViewActionsOptions<TFilters>) {
  // Use unified saved views hook (handles URL sync automatically)
  const {
    views,
    selectedView,
    isDirty,
    handleApplyView: unifiedApplyView,
    handleSaveView: unifiedSaveView,
    handleDeleteView: unifiedDeleteView,
  } = useSavedViewsUnified<TFilters>({
    viewType,
    currentFilters,
    onApplyView: (filters) => {
      onApplyFilter(filters);
      onResetPage();
    },
    includeOrgPresets,
    syncWithUrl: true,
  });

  // Apply view handler - wraps unified handler with clear filters logic
  const applyView = (viewId: string | null) => {
    if (!viewId) {
      onClearAllFilters();
      onResetPage();
    }
    unifiedApplyView(viewId);
  };

  // Save current view handler
  const saveCurrentView = async (name?: string) => {
    await unifiedSaveView(name);
  };

  // Delete current view handler
  const deleteCurrentView = async () => {
    await unifiedDeleteView();
  };

  return {
    views,
    selectedView,
    isDirty,
    applyView,
    saveCurrentView,
    deleteCurrentView,
  };
}
